import type { CSSProperties } from 'react';

/**
 * The decorative auth-screen background, delivered per-client via Module
 * Federation. Modern formats are optional candidates; the PNG is the universal
 * fallback that is always present when a background exists.
 */
export interface BackgroundAssets {
  loginBackground?: string;
  loginBackgroundWebp?: string;
  loginBackgroundAvif?: string;
}

/**
 * Builds the inline `background-image` for the auth screens, preferring the
 * lightest format the browser can decode (AVIF → WebP) and falling back to the
 * PNG. The image is purely decorative, so absence yields an empty style.
 */
export function buildLoginBackgroundStyle(
  assets?: BackgroundAssets | null
): CSSProperties {
  const png = assets?.loginBackground;
  if (!png) {
    return {};
  }

  const candidates = [
    assets?.loginBackgroundAvif
      ? `url("${assets.loginBackgroundAvif}") type("image/avif")`
      : null,
    assets?.loginBackgroundWebp
      ? `url("${assets.loginBackgroundWebp}") type("image/webp")`
      : null,
    `url("${png}") type("image/png")`,
  ].filter((candidate): candidate is string => candidate !== null);

  // With only the PNG present, image-set() adds no value and would drop the
  // background on engines that don't support it — emit a plain url() instead.
  if (candidates.length === 1) {
    return { backgroundImage: `url("${png}")` };
  }

  return { backgroundImage: `image-set(${candidates.join(', ')})` };
}
